<template>
  <div 
    v-if="visible" 
    class="fixed bottom-4 right-4 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto z-[9999] animate-slide-up"
    :class="containerClass"
  >
    <div class="rounded-lg shadow-xs overflow-hidden">
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg :class="iconClass" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPath" />
            </svg>
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm leading-5 font-medium text-gray-900">
              {{ message }}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button 
              @click="close" 
              class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'ToastComponent',
  props: {
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'success',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const visible = ref(true);

    const close = () => {
      visible.value = false;
      emit('close');
    };

    const containerClass = computed(() => {
      switch (props.type) {
        case 'error':
          return 'border-l-4 border-red-500';
        case 'warning':
          return 'border-l-4 border-yellow-500';
        case 'info':
          return 'border-l-4 border-blue-500';
        case 'success':
        default:
          return 'border-l-4 border-green-500';
      }
    });

    const iconClass = computed(() => {
      switch (props.type) {
        case 'error':
          return 'text-red-400';
        case 'warning':
          return 'text-yellow-400';
        case 'info':
          return 'text-blue-400';
        case 'success':
        default:
          return 'text-green-400';
      }
    });

    const iconPath = computed(() => {
      switch (props.type) {
        case 'error':
          return 'M6 18L18 6M6 6l12 12'; // X icon
        case 'warning':
          return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'; // Warning triangle
        case 'info':
          return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'; // Info circle
        case 'success':
        default:
          return 'M5 13l4 4L19 7'; // Check mark
      }
    });

    return {
      visible,
      close,
      containerClass,
      iconClass,
      iconPath,
      t
    };
  }
}
</script>

<style>
@keyframes slide-up {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}

/* Create a stacking context for the toast container */
#app {
  position: relative;
  z-index: 1;
}
</style> 